// src/utils/dateUtils.ts

import { Timestamp } from 'firebase/firestore';
import { getUserData } from '../services/auth';
import { logDev, logError } from './logger';

// 時區緩存（避免頻繁查詢）
const userTimeZoneCache: Record<string, {value: string, expires: number}> = {};
const CACHE_EXPIRY = 1000 * 60 * 60; // 1小時

/**
 * 獲取用戶時區
 * @param userId 用戶ID
 * @returns 用戶設置的時區或瀏覽器默認時區
 */
export const getUserTimeZone = async (userId: string): Promise<string> => {
  const now = Date.now();
  
  // 檢查緩存
  if (userTimeZoneCache[userId] && userTimeZoneCache[userId].expires > now) {
    return userTimeZoneCache[userId].value;
  }
  
  try {
    // 獲取用戶數據
    const userData = await getUserData(userId);
    const timeZone = userData?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // 更新緩存
    userTimeZoneCache[userId] = {
      value: timeZone,
      expires: now + CACHE_EXPIRY
    };
    
    return timeZone;
  } catch (error) {
    logError('getUserTimeZone', '獲取用戶時區失敗:', error);
    // 返回瀏覽器默認時區
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
};

/**
 * 清除時區緩存
 * @param userId 特定用戶ID或清除所有緩存
 */
export const clearTimeZoneCache = (userId?: string): void => {
  if (userId) {
    delete userTimeZoneCache[userId];
  } else {
    Object.keys(userTimeZoneCache).forEach(key => delete userTimeZoneCache[key]);
  }
};

/**
 * 將日期格式化為用戶時區的時間
 * @param date 日期對象
 * @param userId 用戶ID
 * @param options 格式化選項
 * @returns 格式化後的日期字符串
 */
export const formatDateToUserTimeZone = async (
  date: Date,
  userId: string,
  options: Intl.DateTimeFormatOptions = { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }
): Promise<string> => {
  try {
    const timeZone = await getUserTimeZone(userId);
    return new Intl.DateTimeFormat('en-US', {
      ...options,
      timeZone
    }).format(date);
  } catch (error) {
    logError('formatDateToUserTimeZone', '格式化日期失敗:', error);
    // 返回瀏覽器時區的格式化日期
    return new Intl.DateTimeFormat('en-US', options).format(date);
  }
};

/**
 * 日期處理工具模組 - 提供統一的日期轉換和格式化
 */

type DateLike = Date | Timestamp | string | number | {
  toDate?: () => Date;
  _seconds?: number; _nanoseconds?: number;
  seconds?: number; nanoseconds?: number;
} | null | undefined;

// 將任意日期格式轉換為 JavaScript Date 對象
export const toDateObject = (date: DateLike): Date => {
  if (!date) {
    logDev('toDateObject', 'Input is null/undefined, returning current date as fallback');
    // 不要拋出錯誤，返回一個有效的日期作為後備
    return new Date();
  }

  if (date instanceof Date) {
    return date;
  }

  if (date instanceof Timestamp) {
    return date.toDate();
  }

  // 檢查是否為 Firestore Timestamp-like 對象 (有 toDate 方法)
  if (typeof date === 'object' && typeof date.toDate === 'function') {
    return date.toDate!();
  }

  // 處理 Firebase Functions HTTP Callable 序列化的 Timestamp 物件
  //
  // 問題來源：當 Cloud Functions 通過 HTTP Callable 返回包含 Firestore Timestamp 的資料時，
  // Firebase SDK 會將 Timestamp 序列化為普通 JSON 物件：
  // - 格式 1: { _seconds: number, _nanoseconds: number }
  // - 格式 2: { seconds: number, nanoseconds: number }
  //
  // 這些普通物件沒有 .toDate() 方法，會導致 "startDate.toDate is not a function" 錯誤
  //
  // 解決方案：手動將序列化的物件轉換回 JavaScript Date 物件
  // 參考：https://firebase.google.com/docs/reference/js/v8/firebase.firestore.Timestamp
  if (typeof date === 'object' && ('_seconds' in date || 'seconds' in date)) {
    const seconds = date._seconds ?? date.seconds;
    const nanoseconds = date._nanoseconds ?? date.nanoseconds ?? 0;
    if (typeof seconds === 'number') {
      return new Date(seconds * 1000 + Math.floor((nanoseconds as number) / 1000000));
    }
  }

  if (typeof date === 'string') {
    // 嘗試解析 ISO 格式字符串
    const parsedDate = new Date(date);
    if (!isNaN(parsedDate.getTime())) {
      return parsedDate;
    }

    // 如果是其他格式的字符串，可以添加更多解析邏輯
    logDev('toDateObject', 'Failed to parse string date:', date);
    throw new Error(`無法解析日期字符串: ${date}`);
  }

  if (typeof date === 'number') {
    return new Date(date);
  }

  logDev('toDateObject', 'Unsupported date format:', { value: date, type: typeof date });
  throw new Error(`不支持的日期格式: ${typeof date}`);
};

// 將任意日期格式轉換為 Firestore Timestamp
export const toFirestoreTimestamp = (date: DateLike): Timestamp => {
  if (date instanceof Timestamp) {
    return date;
  }
  
  const dateObject = toDateObject(date);
  return Timestamp.fromDate(dateObject);
};

// 格式化日期為 ISO 字符串
export const formatISODate = (date: DateLike): string => {
  try {
    const dateObject = toDateObject(date);
    return dateObject.toISOString();
  } catch (error) {
    logError('formatISODate', '日期格式化錯誤:', error);
    return '';
  }
};

// 格式化日期為本地日期字符串
export const formatLocalDate = (date: DateLike, locale: string = 'en-US'): string => {
  try {
    const dateObject = toDateObject(date);
    return dateObject.toLocaleDateString(locale);
  } catch (error) {
    logError('formatLocalDate', '日期格式化錯誤:', error);
    return '';
  }
};

// 驗證日期是否有效
export const isValidDate = (date: DateLike): boolean => {
  try {
    const dateObject = toDateObject(date);
    return !isNaN(dateObject.getTime());
  } catch (error) {
    return false;
  }
};

/**
 * 根據業務邏輯（凌晨2點為分界）獲取對應的日期ID（yyyy-MM-dd）
 * @param date 可選的日期對象，如果未提供，則使用當前時間
 * @returns 格式為 'yyyy-MM-dd' 的邏輯日期字符串
 * 
 * 此函數會根據凌晨2點分界調整日期，並將其格式化為 'yyyy-MM-dd' 字符串。
 */
export const getLogicalDayId = (date?: Date): string => {
  const targetDate = date ? new Date(date) : new Date();

  // 如果時間是凌晨 0-2 點，我們認為這仍屬於「前一天」
  if (targetDate.getHours() < 2) {
    targetDate.setDate(targetDate.getDate() - 1);
  }

  return formatDateToString(targetDate);
};

/**
 * 格式化日期為 YYYY-MM-DD 格式（使用本地時間避免時區轉換問題）
 * @param date 日期對象
 * @returns 格式為 'YYYY-MM-DD' 的日期字符串
 * 
 * 此函數僅做單純格式化，不考慮業務分界。如需要考慮凌晨2點分界邏輯，請使用 getLogicalDayId。
 */
export const formatDateToString = (date: Date): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * 格式化時間為 HH:MM 格式
 * @param date 日期對象
 * @returns 格式為 'HH:MM' 的時間字符串
 */
export const formatTimeToString = (date: Date): string => {
  return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
};

/**
 * 格式化時間為 12 小時制（如 "11:58 PM"）
 */
export const formatTime12Hour = (date: Date): string => {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12; // 0 小時應顯示為 12

  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
};

/**
 * 格式化相對時間顯示（智能顯示格式）
 * - 今天：顯示時間（如 "9:23 PM"）
 * - 昨天：顯示 "Yesterday, 9:23 PM"
 * - 本週內：顯示星期（如 "Mon, 9:23 PM"）
 * - 更早：顯示日期（如 "Oct 8, 9:23 PM"）
 *
 * @param date 要格式化的日期
 * @returns 格式化後的相對時間字符串
 */
export const formatRelativeTime = (date: Date): string => {
  const now = new Date();
  // 重置時間為當天 00:00:00 以進行日期比較
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const targetDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  // 計算天數差異
  const diffInDays = Math.floor((today.getTime() - targetDate.getTime()) / (1000 * 60 * 60 * 24));

  // 格式化時間部分
  const timeStr = date.toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });

  // 根據天數差異返回不同格式
  if (diffInDays === 0) {
    // 今天：只顯示時間
    return timeStr;
  } else if (diffInDays === 1) {
    // 昨天
    return `Yesterday, ${timeStr}`;
  } else if (diffInDays < 7) {
    // 本週內：顯示星期
    const weekday = date.toLocaleString('en-US', { weekday: 'short' });
    return `${weekday}, ${timeStr}`;
  } else {
    // 更早：顯示月份和日期
    const monthDay = date.toLocaleString('en-US', { month: 'short', day: 'numeric' });
    return `${monthDay}, ${timeStr}`;
  }
};

/**
 * 將 YYYY-MM-DD 日期字串轉換為相對日期標籤
 * 用於 task 的 targetDate / plan 欄位顯示
 * 例：'2026-04-14' → 'Today'、'2026-04-15' → 'Tomorrow'、
 *     '2026-04-12' → 'Overdue 2d'、'2026-04-17' → 'In 3d'
 */
export const formatRelativeDate = (dateStr: string): string => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // 解析 YYYY-MM-DD，避免時區偏移
  const [year, month, day] = dateStr.split('-').map(Number);
  const target = new Date(year, month - 1, day);

  const diffDays = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Overdue 1d';
  if (diffDays < 0) return `Overdue ${Math.abs(diffDays)}d`;
  return `In ${diffDays}d`;
};
